import { IonRefresher, IonRefresherContent, IonInfiniteScroll, IonInfiniteScrollContent, IonIcon, IonModal, IonFab, IonFabButton
} from '@ionic/react';
import { chevronDownCircleOutline, funnelOutline, add, shareOutline } from 'ionicons/icons';
import ShareActivitiesModal from './ShareActivitiesModal';
import { doReload } from '../hooks/common';
import { useEffect, useRef, useState } from 'react';
import useDebounce from "../hooks/useDebounce";
import useNavigation from "../hooks/useNavigation";

import { useLocation } from 'react-router';
import {getSalesActivities} from '../services/sales.service';
import CustomSearchBox from './CustomSearchBox/CustomSearchBox';

import SalesActivities from './SalesActivities';
import SalesActivityFilter from './SalesActivityFilter';
import InteractiveTagBar, { DataItemT } from './GlobalComponents/InteractiveTagBar';
import { useGenericLocalDataStore } from '../store/useGenericLocalDataStore';

const SalesActivityCardList:React.FC = () => {
    const navigateTo = useNavigation();
    const [disableInfiniteScroll, setDisableInfiniteScroll] = useState<boolean>(false);
    const [salesActivities, setSalesActivities] = useState([] as any[]);
    
    const [searchText, setSearchText] = useState('');
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [filterData, setFilterData] = useState<any>();
    const [skip, setSkip] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const location = useLocation();
    const debounceSearchText = useDebounce(searchText, 1000);
    const isMounted = useRef(false);

    // Saved filters state
    const [savedFilters, setSavedFilters] = useState<any[]>([]);
    const [showFilterNameInModal, setShowFilterNameInModal] = useState(false);
    const [activeFilt, setActiveFilt] = useState<DataItemT>({ id: "" });
    const [processedDatas, setProcessedData] = useState<DataItemT[]>();

    // Local storage
    const { setItem, getItem, rehydrateStore } = useGenericLocalDataStore.getState();
    const { data, isDataLoaded } = useGenericLocalDataStore((state) => state);

    // Reset pagination when search or filter changes
    useEffect(() => {
        setSkip(0);
        setDisableInfiniteScroll(false);
        setSalesActivities([]); // Clear existing data
        fetchActivities(debounceSearchText, 0);
    }, [debounceSearchText, filterData]);
    
    // Handle reload from navigation (skip on initial mount — the main effect already covers that)
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        if ((location?.state as any)?.reload) {
            setSkip(0);
            setDisableInfiniteScroll(false);
            setSalesActivities([]);
            fetchActivities();
            (location?.state as any).reload = false;
        }
    }, [location?.state]);

    // Rehydrate store and load saved filters
    const rehydrateData = async () => {
        if (!isDataLoaded) {
            try {
                await rehydrateStore();
            } catch (err) {
                console.log("No saved filters", err);
            }
        }
    };

    useEffect(() => {
        rehydrateData();
    }, []);

    // Load saved filters when data is available
    useEffect(() => {
        if (isDataLoaded && data) {
            const saved = data["sales_activity_saved_filters"];
            if (saved && Array.isArray(saved)) {
                setSavedFilters(saved);
                setProcessedData(processData(saved));
            }
        }
    }, [isDataLoaded, data]);

    // Process data for InteractiveTagBar
    const processData = (data: any[]) => {
        return data?.map(function (item: any) {
            return {
                id: item.filterName,
            };
        });
    };

    const fetchActivities = async (text: string = '', skipValue: number = 0) => {
        setIsLoading(true);
        try {
            const items = await getSalesActivities(text, [], skipValue, filterData);
            if (skipValue === 0) {
                // Initial load or reset
                setSalesActivities(items || []);
            } else {
                // Append for pagination
                setSalesActivities(prev => [...prev, ...(items || [])]);
            }
            
            // Disable infinite scroll if we got less than 20 items (indicating no more data)
            setDisableInfiniteScroll(!items || items.length < 20);
        } catch (error) {
            console.error('Failed to fetch activities:', error);
            setDisableInfiniteScroll(true);
        } finally {
            setIsLoading(false);
        }
    };

    async function fetchNext($event: CustomEvent<void>) {
        if (isLoading) {
            ($event.target as HTMLIonInfiniteScrollElement).complete();
            return;
        }

        const nextSkip = skip + 20;
        setSkip(nextSkip);
        
        try {
            const items = await getSalesActivities(debounceSearchText, [], nextSkip, filterData);
            
            if (items && items.length > 0) {
                setSalesActivities(prev => [...prev, ...items]);
                // Disable infinite scroll if we got less than 20 items
                setDisableInfiniteScroll(items.length < 20);
            } else {
                // No more data
                setDisableInfiniteScroll(true);
            }
        } catch (error) {
            console.error('Failed to fetch next page:', error);
            setDisableInfiniteScroll(true);
        } finally {
            ($event.target as HTMLIonInfiniteScrollElement).complete();
        }
    }

    // Filter handling functions
    const handleFilterForModal = (filter: any) => {
        const { filterName, type, group, location, servicedBy } = filter;
        
        // Save filter if name provided
        if (filterName) {
            const dataToSave = {
                filterName: filterName,
                type: type,
                group: group,
                location: location,
                servicedBy: servicedBy,
            };
            
            const updatedFilters = [...savedFilters, dataToSave];
            setItem("sales_activity_saved_filters", updatedFilters);
            setSavedFilters(updatedFilters);
            setProcessedData(processData(updatedFilters));
        }
        
        // Apply filter immediately
        setFilterData({ type, group, location, servicedBy });
    };

    const handleAddIcon = () => {
        setShowFilterNameInModal(true);
        setShowFilterModal(true);
    };

    const handleItemClick = (e: React.MouseEvent, item: any) => {
        if (item.id === activeFilt.id) { return; }
        const itemClicked = savedFilters.filter((it) => it.filterName === item.id);
        if (itemClicked.length > 0) {
            const { type, group, location, servicedBy } = itemClicked[0];
            setFilterData({ type, group, location, servicedBy });
            setActiveFilt({ id: item.id });
        }
    };

    const handleRemoveItem = async (item: DataItemT) => {
        const updatedSavedFilters = savedFilters.filter(
            (it) => it.filterName !== item.id
        );
        setItem("sales_activity_saved_filters", updatedSavedFilters);
        setSavedFilters(updatedSavedFilters);
        setProcessedData(processData(updatedSavedFilters));
        setActiveFilt({ id: "" });
    };

    return (
        <>
        <div className="searchBarContainer">
            <div className="sbContainer">
                <CustomSearchBox setSearchText={setSearchText} />
            </div>
            <div className="iconContainer">
                <IonIcon
                    icon={funnelOutline}
                    slot="end"
                    onClick={(e) => setShowFilterModal(true)}
                ></IonIcon>
            </div>
        </div>

        {/* Interactive Tag Bar for Saved Filters */}
        <InteractiveTagBar
            items={processedDatas}
            activeItem={activeFilt}
            onItemClick={handleItemClick}
            onRemoveItem={handleRemoveItem}
            deleteIcon={true}
            showAddIcon={true}
            onAddItem={handleAddIcon}
        />
        
        <IonRefresher slot="fixed" onIonRefresh={doReload}>
            <IonRefresherContent
                pullingIcon={chevronDownCircleOutline}
                pullingText="Pull to refresh"
                refreshingSpinner="circles"
                refreshingText="Refreshing...">
            </IonRefresherContent>
        </IonRefresher>
        
        <SalesActivities salesActivity={salesActivities} />
        
        <IonInfiniteScroll 
            threshold="100px"
            disabled={disableInfiniteScroll || isLoading}
            onIonInfinite={(e: CustomEvent<void>) => fetchNext(e)}
        >
            <IonInfiniteScrollContent
                loadingText={isLoading ? "Loading..." : "Loading more..."}>
            </IonInfiniteScrollContent>
        </IonInfiniteScroll>

        <IonModal isOpen = {showFilterModal} cssClass="my-custom-class">
            <SalesActivityFilter 
                filter={filterData}
                filterChangeHandler={handleFilterForModal}
                onDoneHandler={() => {}}
                onCloseHandler={() => {
                    if (showFilterNameInModal) {
                        setShowFilterNameInModal(false);
                    }
                    setShowFilterModal(false);
                }}
                showFilterNameInModal={showFilterNameInModal}
            ></SalesActivityFilter>
        </IonModal>

        {/* Share FAB — sits above the add button */}
        <IonFab vertical="bottom" horizontal="end" slot="fixed" style={{ bottom: '80px' }}>
            <IonFabButton onClick={() => setShowShareModal(true)}>
                <IonIcon icon={shareOutline} />
            </IonFabButton>
        </IonFab>

        {/* Add Activity FAB */}
        <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton onClick={() => navigateTo("/tabs/salesActivity", { newActivity: true })}>
                <IonIcon icon={add} />
            </IonFabButton>
        </IonFab>

        <IonModal isOpen={showShareModal}>
            <ShareActivitiesModal onClose={() => setShowShareModal(false)} />
        </IonModal>
        </>
    )
}

export default SalesActivityCardList; 

